import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

const PAGES = [
  { path: "/", label: "./home" },
  { path: "/work", label: "./work" },
  { path: "/gallery", label: "./gallery" },
  { path: "/contact", label: "./contact" },
];

const SOCIALS = [
  { href: "https://github.com/saketkhundia", label: "GitHub", viewBox: "0 0 24 24", paths: [{ d: "M12 .5C5.65.5.5 5.65.5 12c0 5.1 3.29 9.42 7.86 10.95.57.1.78-.25.78-.55v-2.1c-3.2.7-3.87-1.54-3.87-1.54-.52-1.33-1.28-1.68-1.28-1.68-1.04-.71.08-.7.08-.7 1.15.08 1.76 1.18 1.76 1.18 1.02 1.75 2.68 1.24 3.33.95.1-.74.4-1.24.72-1.53-2.55-.29-5.23-1.28-5.23-5.68 0-1.25.45-2.28 1.18-3.08-.12-.29-.51-1.46.11-3.04 0 0 .96-.31 3.15 1.18a10.94 10.94 0 0 1 5.74 0c2.18-1.49 3.14-1.18 3.14-1.18.63 1.58.24 2.75.12 3.04.74.8 1.18 1.83 1.18 3.08 0 4.41-2.69 5.38-5.25 5.67.41.36.78 1.06.78 2.13v3.16c0 .3.2.66.79.55C20.22 21.41 23.5 17.1 23.5 12 23.5 5.65 18.35.5 12 .5z" }] },
  { href: "https://www.linkedin.com/in/saketkhundia", label: "LinkedIn", viewBox: "0 0 24 24", paths: [{ d: "M20.45 20.45h-3.55v-5.57c0-1.33-.03-3.04-1.85-3.04-1.86 0-2.14 1.45-2.14 2.95v5.66H9.36V9h3.41v1.56h.05c.47-.9 1.63-1.85 3.36-1.85 3.59 0 4.26 2.37 4.26 5.45v6.29zM5.34 7.43a2.06 2.06 0 1 1 0-4.12 2.06 2.06 0 0 1 0 4.12zM7.12 20.45H3.56V9h3.56v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.72V1.72C24 .77 23.2 0 22.22 0z" }] },
  { href: "mailto:saketkhundia21@gmail.com", label: "Email", viewBox: "0 0 24 24", stroke: true, paths: [{ tag: "rect", x: 2, y: 4, width: 20, height: 16, rx: 2 }, { d: "m2 7 10 7 10-7" }] },
];

// Each link types its characters one-by-one
const linkVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.04,   // 40ms between characters
    },
  },
};

// Each character fades in
const charVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
};

// Container starts each link only after the previous one finishes
const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.3,    // 300ms between links
    },
  },
};

const MotionLink = motion(Link);

function SocialIcon({ href, label, viewBox, paths, stroke }) {
  return (
    <a href={href} target="_blank" rel="noreferrer" className="mob-social-link" aria-label={label}>
      <svg viewBox={viewBox} width="22" height="22" fill={stroke ? "none" : "currentColor"} stroke={stroke ? "currentColor" : "none"} strokeWidth={stroke ? "1.8" : "0"}>
        {paths.map((p, i) =>
          p.tag === "rect" ? (
            <rect key={i} x={p.x} y={p.y} width={p.width} height={p.height} rx={p.rx} />
          ) : (
            <path key={i} d={p.d} />
          )
        )}
      </svg>
    </a>
  );
}

export default function MobileMenu({ open, onClose, theme = "dark", onToggleTheme }) {
  const location = useLocation();

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="mob-menu-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={onClose}
        >
          <motion.nav
            className="mob-menu-panel"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            onClick={(e) => e.stopPropagation()}
          >
            {PAGES.map((item) => (
              <MotionLink
                key={item.path}
                to={item.path}
                className={`mob-menu-link${location.pathname === item.path ? " is-active" : ""}`}
                variants={linkVariants}
                onClick={onClose}
                aria-current={location.pathname === item.path ? "page" : undefined}
              >
                {item.label.split("").map((char, i) => (
                  <motion.span key={i} variants={charVariants} style={{ display: "inline-block" }}>
                    {char}
                  </motion.span>
                ))}
              </MotionLink>
            ))}
            <button
              className="mob-theme-toggle"
              onClick={() => {
                if (onToggleTheme) onToggleTheme();
                onClose();
              }}
              aria-label={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
            >
              {theme === "dark" ? "[ switch to light ]" : "[ switch to dark ]"}
            </button>
          </motion.nav>

          {/* Social icons — bottom center */}
          <div className="mob-socials">
            {SOCIALS.map((s) => (
              <SocialIcon key={s.label} {...s} />
            ))}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
